import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Plus, Trash2, Loader2, Lock, Upload, Sparkles, Settings2, ExternalLink, Download, ArrowRight } from "lucide-react";
import html2canvas from "html2canvas";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { SaudiRiyal, Money } from "./SaudiRiyal";
import { Letterhead } from "./Letterhead";
import { api, fileUrl } from "../lib/api";
import { InvoicesHub } from "./InvoicesHub";
import { RiderCostCard } from "./RiderCostCard";
import { MonthlyComparisonCard } from "./MonthlyComparisonCard";
import { FixedCostsCard } from "./FixedCostsCard";
import { RiderProfitCard } from "./RiderProfitCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { TargetSettingsDialog } from "./TargetSettingsDialog";
import { Target } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const COLORS = ["#065f46", "#f59e0b", "#2563eb", "#db2777", "#7c3aed", "#0891b2", "#dc2626", "#15803d"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

// Shift a YYYY-MM-DD date back by exactly one month (keeps the day, rolls the year)
const shiftBackOneMonth = (d) => {
  const parts = String(d || "").split("-");
  if (parts.length < 2) return d;
  let y = Number(parts[0]); let m = Number(parts[1]); const day = parts[2] || "01";
  m -= 1; if (m < 1) { m = 12; y -= 1; }
  return `${y}-${String(m).padStart(2, "0")}-${day}`;
};

// ---------- Lock gate ----------
const LOCKS = {
  revenue: { cfg: () => api.revenueConfig(), set: (p) => api.setRevenueLock(p), verify: (p) => api.verifyRevenueLock(p), title: "قسم الإيراد مقفل", done: "تم ضبط كلمة سر قسم الإيراد" },
  salary: { cfg: () => api.salaryLockConfig(), set: (p) => api.setSalaryLock(p), verify: (p) => api.verifySalaryLock(p), title: "قسم الرواتب مقفل", done: "تم ضبط كلمة سر قسم الرواتب" },
};
const LockReset = ({ kind, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ login: "", p1: "", p2: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!/^\d{6}$/.test(f.p1)) { toast.warning("الرمز الجديد يجب أن يكون 6 أرقام بالضبط"); return; }
    if (f.p1 !== f.p2) { toast.warning("الرمزان غير متطابقين"); return; }
    setBusy(true);
    try { await api.resetSectionLock(kind, f.login, f.p1); toast.success("تم تغيير الرمز السري"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تغيير الرمز"); } finally { setBusy(false); }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="mt-3 text-xs font-semibold text-slate-500 hover:text-emerald-700" data-testid={`${kind}-lock-forgot`}>نسيت الرمز أو تبي تغيّره؟</button>;
  return (
    <div className="mt-4 space-y-2 rounded-2xl bg-slate-50 p-3 text-right" data-testid={`${kind}-lock-reset`}>
      <p className="text-[11px] text-slate-500">للمدير فقط: أدخل كلمة مرور دخولك للتطبيق ثم الرمز الجديد.</p>
      <Input type="password" value={f.login} onChange={(e) => setF({ ...f, login: e.target.value })} placeholder="كلمة مرور الدخول" autoComplete="current-password" data-testid={`${kind}-lock-login`} />
      <Input pin value={f.p1} onChange={(e) => setF({ ...f, p1: e.target.value })} placeholder="الرمز الجديد (6 أرقام)" data-testid={`${kind}-lock-new`} />
      <Input pin value={f.p2} onChange={(e) => setF({ ...f, p2: e.target.value })} placeholder="تأكيد الرمز الجديد" data-testid={`${kind}-lock-new2`} />
      <Button className="w-full" variant="outline" disabled={busy || !f.login || !f.p1} onClick={save} data-testid={`${kind}-lock-reset-save`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} تغيير الرمز</Button>
    </div>
  );
};

export const LockGate = ({ onUnlock, kind = "revenue" }) => {
  const L = LOCKS[kind];
  const { dir } = useLang();
  const [cfg, setCfg] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { L.cfg().then(setCfg).catch(() => setCfg({ lock_set: false })); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      if (cfg.lock_set) {
        await L.verify(pwd);
        onUnlock();
      } else {
        if (!/^\d{6}$/.test(pwd)) { toast.warning("الرمز السري يجب أن يكون 6 أرقام بالضبط"); setBusy(false); return; }
        if (pwd !== pwd2) { toast.warning("كلمتا السر غير متطابقتين"); setBusy(false); return; }
        await L.set(pwd);
        toast.success(L.done);
        onUnlock();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "كلمة السر غير صحيحة"); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  return (
    <div className="mx-auto max-w-sm py-12" dir={dir} data-testid={`${kind}-lock-gate`}>
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600"><Lock className="h-7 w-7" /></div>
        <h2 className="mt-4 font-heading text-xl font-bold text-slate-900">{L.title}</h2>
        <p className="mt-1 text-sm text-slate-500">{cfg.lock_set ? "أدخل كلمة السر للدخول" : "اضبط كلمة سر لحماية هذا القسم (أول مرة)"}</p>
        <div className="mt-5 space-y-3 text-right">
          <Input data-testid={`${kind}-lock-input`} pin name={`${kind}-lock-pwd`} value={pwd} onChange={(e) => setPwd(e.target.value)}
            placeholder={cfg.lock_set ? "كلمة السر" : "كلمة سر جديدة"} onKeyDown={(e) => e.key === "Enter" && cfg.lock_set && submit()} autoFocus />
          {!cfg.lock_set && (
            <Input data-testid={`${kind}-lock-confirm`} pin value={pwd2} onChange={(e) => setPwd2(e.target.value)}
              placeholder="تأكيد كلمة السر" onKeyDown={(e) => e.key === "Enter" && submit()} />
          )}
        </div>
        <Button data-testid={`${kind}-lock-submit`} disabled={busy || !pwd} onClick={submit} className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {cfg.lock_set ? "دخول" : "ضبط ودخول"}
        </Button>
        {cfg.lock_set && <LockReset kind={kind} onDone={onUnlock} />}
      </div>
    </div>
  );
};

// ---------- Entry dialog ----------
const EntryDialog = ({ open, onOpenChange, platforms, initial, onSaved }) => {
  const { dir } = useLang();
  const [form, setForm] = useState({ platform: "", amount: "", revenue_date: "", invoice_number: "", note: "", photo_id: null, photo_name: null });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({
      platform: initial?.platform || "", amount: initial?.amount ?? "", revenue_date: initial?.revenue_date || new Date().toISOString().slice(0, 10),
      invoice_number: initial?.invoice_number || "", note: initial?.note || "", photo_id: initial?.photo_id || null, photo_name: initial?.photo_name || null,
    });
  }, [open, initial]);

  const save = async () => {
    if (!form.platform) { toast.warning("اختر المنصة"); return; }
    setBusy(true);
    try {
      if (initial?.id) await api.updateRevenue(initial.id, { ...form, amount: Number(form.amount || 0) });
      else await api.createRevenue({ ...form, amount: Number(form.amount || 0) });
      toast.success("تم الحفظ"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="revenue-entry-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">{initial?.id ? "تعديل إيراد" : "إضافة إيراد"}</DialogTitle>
          <DialogDescription className="text-right">اختر المنصة وأدخل المبلغ والتاريخ.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>المنصة</Label>
            <select data-testid="revenue-platform-select" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">اختر المنصة</option>
              {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="flex items-center gap-1">المبلغ <SaudiRiyal className="text-slate-400" /></Label>
              <Input data-testid="revenue-amount-input" type="number" dir="ltr" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>التاريخ</Label>
              <Input data-testid="revenue-date-input" type="date" value={form.revenue_date} onChange={(e) => setForm({ ...form, revenue_date: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>رقم الفاتورة (اختياري)</Label>
            <Input dir="ltr" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>ملاحظة (اختياري)</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          {form.photo_id && <div className="text-xs text-emerald-600">📎 مرفق فاتورة: {form.photo_name}</div>}
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="revenue-save-btn" disabled={busy} onClick={save} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} حفظ
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KPI = ({ label, children, tone }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`font-heading text-xl font-bold ${tone || "text-slate-800"}`}>{children}</p>
  </div>
);

const RevenueBody = ({ canEdit }) => {
  const { dir } = useLang();
  const [data, setData] = useState({ items: [], summary: {} });
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [importing, setImporting] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [invoiceMonth, setInvoiceMonth] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [platCfgOpen, setPlatCfgOpen] = useState(false);
  const [targetOpen, setTargetOpen] = useState(false);
  const [newPlat, setNewPlat] = useState("");
  const [exporting, setExporting] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const reportRef = useRef(null);
  const recordsRef = useRef(null);
  const [month, setMonth] = useState("");

  const exportReport = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `تقرير-الإيراد-${year}.png`;
      a.click();
      toast.success("تم تصدير تقرير الإيراد كصورة");
    } catch (e) { toast.error("تعذّر إنشاء الصورة"); }
    finally { setExporting(false); }
  };

  const load = useCallback(async () => {
    try {
      const [rev, cfg] = await Promise.all([api.listRevenues(), api.revenueConfig()]);
      setData(rev); setPlatforms(cfg.platforms || []);
    } catch { toast.error("تعذّر تحميل الإيرادات"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const years = useMemo(() => {
    const ys = new Set(data.items.map((r) => (r.revenue_date || "").slice(0, 4)).filter(Boolean));
    ys.add(String(new Date().getFullYear()));
    return Array.from(ys).sort().reverse();
  }, [data.items]);

  const yearItems = useMemo(() => year === "all" ? data.items : data.items.filter((r) => (r.revenue_date || "").slice(0, 4) === String(year)), [data.items, year]);

  // monthly per platform + total
  const monthly = useMemo(() => {
    const rows = MONTHS_AR.map((m, i) => ({ month: m, idx: i, total: 0 }));
    platforms.forEach((p) => rows.forEach((r) => (r[p] = 0)));
    yearItems.forEach((r) => {
      const mi = parseInt((r.revenue_date || "").slice(5, 7), 10) - 1;
      if (mi >= 0 && mi < 12) { rows[mi].total += Number(r.amount || 0); rows[mi][r.platform] = (rows[mi][r.platform] || 0) + Number(r.amount || 0); }
    });
    return rows;
  }, [yearItems, platforms]);

  const quarterly = useMemo(() => {
    const rows = QUARTERS.map((q) => ({ quarter: q, total: 0 }));
    platforms.forEach((p) => rows.forEach((r) => (r[p] = 0)));
    yearItems.forEach((r) => {
      const mi = parseInt((r.revenue_date || "").slice(5, 7), 10) - 1;
      const qi = Math.floor(mi / 3);
      if (qi >= 0 && qi < 4) { rows[qi].total += Number(r.amount || 0); rows[qi][r.platform] = (rows[qi][r.platform] || 0) + Number(r.amount || 0); }
    });
    return rows;
  }, [yearItems, platforms]);

  const byPlatform = useMemo(() => {
    const map = {};
    yearItems.forEach((r) => { map[r.platform] = (map[r.platform] || 0) + Number(r.amount || 0); });
    return Object.entries(map).map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 })).sort((a, b) => b.value - a.value);
  }, [yearItems]);

  const yearTotal = useMemo(() => byPlatform.reduce((a, b) => a + b.value, 0), [byPlatform]);

  const onImport = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setImporting(true);
    try { const r = await api.importRevenues((() => { const fd = new FormData(); fd.append("file", f); return fd; })()); toast.success(`تم استيراد ${r.inserted} سجل${r.skipped ? ` — تم تجاهل ${r.skipped} مكرّر` : ""}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الاستيراد"); }
    finally { setImporting(false); }
  };

  const onInvoice = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setExtracting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.extractRevenueInvoice(fd);
      let rev_date = r.revenue_date;
      let shifted = false;
      if (invoiceMonth) {
        // User picked a month explicitly -> use it (keep AI-read day if present)
        const day = (r.revenue_date || "").slice(8, 10) || "01";
        rev_date = `${invoiceMonth}-${day}`;
      } else if (rev_date) {
        // No month picked -> AI reads the date, but invoices are for the previous month
        rev_date = shiftBackOneMonth(rev_date);
        shifted = true;
      }
      setEditItem({ platform: r.platform, amount: r.amount, revenue_date: rev_date, invoice_number: r.invoice_number, photo_id: r.photo_id, photo_name: r.photo_name });
      setEntryOpen(true);
      toast.success(`تمت قراءة الفاتورة${r.amount ? ` — المبلغ ${r.amount}` : ""}.${shifted ? " تم اعتماد الشهر السابق تلقائيًا." : ""} راجع البيانات واحفظ.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر قراءة الفاتورة"); }
    finally { setExtracting(false); }
  };

  const addPlatform = async () => {
    if (!newPlat.trim()) return;
    try { const r = await api.addRevenuePlatform(newPlat.trim()); setPlatforms(r.platforms); setNewPlat(""); toast.success("تمت إضافة المنصة"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإضافة"); }
  };

  const del = async (id) => { try { await api.deleteRevenue(id); toast.success("تم الحذف"); load(); } catch { toast.error("تعذّر الحذف"); } };

  const chartFmt = (v) => Number(v || 0).toLocaleString("en-US");

  const pColor = COLORS[Math.max(0, platforms.indexOf(selectedPlatform)) % COLORS.length];
  const pMonthly = monthly.map((m) => ({ month: m.month, value: m[selectedPlatform] || 0 }));
  const pQuarterly = quarterly.map((q) => ({ quarter: q.quarter, value: q[selectedPlatform] || 0 }));
  const pItems = yearItems.filter((r) => r.platform === selectedPlatform);
  const pYearTotal = byPlatform.find((b) => b.name === selectedPlatform)?.value || 0;
  const pAllTotal = data.summary?.by_platform?.[selectedPlatform] || 0;

  // Month-over-month growth for the selected platform
  const nowDate = new Date();
  const gCurIdx = year === nowDate.getFullYear() ? nowDate.getMonth() : 11;
  const gPrevIdx = gCurIdx - 1;
  const gCurVal = pMonthly[gCurIdx]?.value || 0;
  const gPrevVal = gPrevIdx >= 0 ? (pMonthly[gPrevIdx]?.value || 0) : 0;
  const gHasPrev = gPrevIdx >= 0;
  const gDiff = gCurVal - gPrevVal;
  const gPct = gPrevVal > 0 ? (gDiff / gPrevVal) * 100 : (gCurVal > 0 ? 100 : 0);
  const gUp = gDiff >= 0;

  // Overall month-over-month growth (all platforms) for the overview page
  const gTotCurVal = monthly[gCurIdx]?.total || 0;
  const gTotPrevVal = gPrevIdx >= 0 ? (monthly[gPrevIdx]?.total || 0) : 0;
  const gTotDiff = gTotCurVal - gTotPrevVal;
  const gTotPct = gTotPrevVal > 0 ? (gTotDiff / gTotPrevVal) * 100 : (gTotCurVal > 0 ? 100 : 0);
  const gTotUp = gTotDiff >= 0;
  const yearLabel = year === "all" ? "كل السنوات" : year;
  const recRows = month ? yearItems.filter((r) => (r.revenue_date || "").slice(5, 7) === month) : yearItems;
  const recTotal = recRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div className="space-y-4" dir={dir} data-testid="revenue-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><TrendingUp className="h-5 w-5" /></div>
          <div><h2 className="font-heading text-lg font-bold text-slate-900">الإيراد</h2>
            <p className="text-xs text-slate-500">إيرادات منصات التوصيل وتحليلها</p></div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="revenue-year" value={year} onChange={(e) => setYear(e.target.value === "all" ? "all" : Number(e.target.value))} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
              <option value="all">كل السنوات</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select data-testid="revenue-month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
              <option value="">كل الأشهر</option>
              {MONTHS_AR.map((m, i) => <option key={i} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
            </select>
            <Button variant="outline" onClick={() => setPlatCfgOpen(true)} className="gap-1.5 border-slate-200 text-slate-700" data-testid="revenue-platforms-btn"><Settings2 className="h-4 w-4" /> المنصات</Button>
            <Button variant="outline" onClick={() => setTargetOpen(true)} className="gap-1.5 border-indigo-200 text-indigo-700" data-testid="revenue-target-btn"><Target className="h-4 w-4" /> إعدادات التارقت</Button>
            <Button variant="outline" disabled={exporting} onClick={exportReport} className="gap-1.5 border-slate-200 text-slate-700" data-testid="revenue-export-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تصدير التقرير
            </Button>
            <input type="file" accept="image/*,.pdf" id="rev-invoice" className="hidden" data-testid="revenue-invoice-input" onChange={onInvoice} />
            <div className="flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50/50 px-1.5 py-0.5">
              <Input type="month" data-testid="revenue-invoice-month" value={invoiceMonth} onChange={(e) => setInvoiceMonth(e.target.value)}
                className="h-8 rounded-md border-0 bg-transparent px-1 text-xs text-indigo-800 focus:outline-none" title="شهر الفاتورة (اختياري)" />
              <Button variant="outline" disabled={extracting} onClick={() => document.getElementById("rev-invoice").click()} className="h-8 gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="revenue-invoice-btn">
                {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} رفع فاتورة
              </Button>
            </div>
            <input type="file" accept=".xlsx,.xls" id="rev-import" className="hidden" data-testid="revenue-import-input" onChange={onImport} />
            <Button variant="outline" disabled={importing} onClick={() => document.getElementById("rev-import").click()} className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="revenue-import-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} استيراد Excel
            </Button>
            <Button onClick={() => { setEditItem(null); setEntryOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="revenue-add-btn"><Plus className="h-4 w-4" /> إضافة إيراد</Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="summary" dir={dir} className="space-y-4">
        <TabsList data-testid="revenue-tabs" className="grid h-auto w-full grid-cols-3 gap-1 p-1 sm:grid-cols-5">
          {[["summary", "ملخص"], ["profit", "ربحية المناديب"], ["fixed", "المصروفات الثابتة"], ["variable", "المصروفات المتغيرة"], ["invoices", "الفواتير والمنصات"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`revenue-tab-${v}`} className="whitespace-normal px-1.5 py-1.5 text-[11px] leading-tight sm:text-sm">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="profit"><RiderProfitCard /></TabsContent>
        <TabsContent value="fixed"><FixedCostsCard canEdit={canEdit} /></TabsContent>
        <TabsContent value="variable"><RiderCostCard canEdit={canEdit} /></TabsContent>
        <TabsContent value="invoices"><InvoicesHub canEdit={canEdit} /></TabsContent>
        <TabsContent value="summary" className="space-y-4">
      <MonthlyComparisonCard />

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : selectedPlatform ? (
        <div className="space-y-4" data-testid="revenue-platform-detail">
          <button data-testid="revenue-back" onClick={() => setSelectedPlatform(null)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowRight className="h-4 w-4" /> رجوع للنظرة العامة</button>
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ background: pColor }} />
            <h3 className="font-heading text-lg font-bold text-slate-900">{selectedPlatform}</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label={`إجمالي ${yearLabel}`} tone="text-emerald-700"><Money value={pYearTotal} /></KPI>
            <KPI label="الإجمالي الكلي"><Money value={pAllTotal} /></KPI>
            <KPI label="عدد السجلات">{pItems.length}</KPI>
            <KPI label="متوسط شهري"><Money value={Math.round(pYearTotal / 12)} /></KPI>
          </div>
          {/* Month-over-month growth card */}
          <div data-testid="revenue-growth-card" className={`flex items-center justify-between gap-3 rounded-2xl border p-4 ${gUp ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
            <div>
              <p className="text-xs text-slate-500">نمو الإيراد — {MONTHS_AR[gCurIdx]} مقابل {gHasPrev ? MONTHS_AR[gPrevIdx] : "—"}</p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-heading text-2xl font-bold text-slate-900"><Money value={gCurVal} /></span>
                {gHasPrev && (
                  <span data-testid="revenue-growth-pct" className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-sm font-bold ${gUp ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {gUp ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                    {gUp ? "+" : ""}{gPct.toFixed(1)}%
                  </span>
                )}
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                {gHasPrev ? <>الشهر السابق ({MONTHS_AR[gPrevIdx]}): <Money value={gPrevVal} /> · الفرق: {gUp ? "+" : ""}<Money value={gDiff} /></> : "لا يوجد شهر سابق للمقارنة في هذه السنة"}
              </p>
            </div>
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${gUp ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>
              {gUp ? <TrendingUp className="h-6 w-6" /> : <TrendingDown className="h-6 w-6" />}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="mb-3 font-heading text-sm font-bold text-slate-800">التحليل الشهري — {selectedPlatform} ({year})</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={pMonthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={chartFmt} width={54} />
                <Tooltip formatter={(v) => `${chartFmt(v)} ⃁`} />
                <Line type="monotone" dataKey="value" name={selectedPlatform} stroke={pColor} strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="mb-3 font-heading text-sm font-bold text-slate-800">التحليل الربعي — {selectedPlatform} ({year})</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={pQuarterly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="quarter" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={chartFmt} width={54} />
                <Tooltip formatter={(v) => `${chartFmt(v)} ⃁`} />
                <Bar dataKey="value" name={selectedPlatform} fill={pColor} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-slate-50 text-slate-500"><tr>
                <th className="px-3 py-2.5">التاريخ</th><th className="px-3 py-2.5">المبلغ</th><th className="px-3 py-2.5">الفاتورة</th>{canEdit && <th className="px-3 py-2.5">إجراءات</th>}
              </tr></thead>
              <tbody>
                {pItems.length === 0 ? (
                  <tr><td colSpan={4} className="py-12 text-center text-slate-400">لا توجد سجلات لهذه المنصة في {year}</td></tr>
                ) : pItems.map((r) => (
                  <tr key={r.id} data-testid={`revenue-prow-${r.id}`} className="border-t border-slate-100">
                    <td className="px-3 py-2.5 text-center font-mono-nums text-slate-600" dir="ltr">{r.revenue_date}</td>
                    <td className="px-3 py-2.5 text-center font-semibold"><Money value={r.amount} /></td>
                    <td className="px-3 py-2.5 text-center">{r.photo_id ? <a href={fileUrl(r.photo_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs"><ExternalLink className="h-3.5 w-3.5" /> {r.invoice_number || "عرض"}</a> : (r.invoice_number || <span className="text-slate-300">—</span>)}</td>
                    {canEdit && <td className="px-3 py-2.5"><div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => { setEditItem(r); setEntryOpen(true); }} className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100">تعديل</button>
                      <button onClick={() => del(r.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </div></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div ref={reportRef} className="space-y-4 bg-white p-3 rounded-2xl">
          <Letterhead />
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <div className="font-heading text-base font-bold text-emerald-700">تقرير الإيراد — {yearLabel}</div>
            <div className="text-xs text-slate-400">{new Date().toLocaleDateString("ar-SA")}</div>
          </div>
          {/* Overall month-over-month growth card */}
          <div data-testid="revenue-total-growth-card" className={`flex items-center justify-between gap-3 rounded-2xl border p-4 ${gTotUp ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
            <div>
              <p className="text-xs text-slate-500">إجمالي نمو الإيراد — {MONTHS_AR[gCurIdx]} مقابل {gHasPrev ? MONTHS_AR[gPrevIdx] : "—"}</p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-heading text-2xl font-bold text-slate-900"><Money value={gTotCurVal} /></span>
                {gHasPrev && (
                  <span data-testid="revenue-total-growth-pct" className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-sm font-bold ${gTotUp ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {gTotUp ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                    {gTotUp ? "+" : ""}{gTotPct.toFixed(1)}%
                  </span>
                )}
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                {gHasPrev ? <>الشهر السابق ({MONTHS_AR[gPrevIdx]}): <Money value={gTotPrevVal} /> · الفرق: {gTotUp ? "+" : ""}<Money value={gTotDiff} /></> : "لا يوجد شهر سابق للمقارنة في هذه السنة"}
              </p>
            </div>
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${gTotUp ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>
              {gTotUp ? <TrendingUp className="h-6 w-6" /> : <TrendingDown className="h-6 w-6" />}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <button type="button" data-testid="revenue-total-card" onClick={() => recordsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="text-right transition hover:opacity-80 active:scale-95">
              <KPI label={`إجمالي ${yearLabel} — عرض التقرير ›`} tone="text-emerald-700"><Money value={yearTotal} /></KPI>
            </button>
            <KPI label="الإجمالي الكلي" tone="text-slate-800"><Money value={data.summary?.total} /></KPI>
            <KPI label="عدد السجلات">{data.summary?.count || 0}</KPI>
            <KPI label="أعلى منصة" tone="text-blue-600">{byPlatform[0]?.name || "—"}</KPI>
          </div>

          {/* Monthly line per platform */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="mb-3 font-heading text-sm font-bold text-slate-800">التحليل الشهري — إيراد كل منصة ({yearLabel})</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={chartFmt} width={54} />
                <Tooltip formatter={(v) => `${chartFmt(v)} ⃁`} /><Legend wrapperStyle={{ fontSize: 12 }} />
                {platforms.map((p, i) => <Line key={p} type="monotone" dataKey={p} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Quarterly bars total */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="mb-3 font-heading text-sm font-bold text-slate-800">التحليل الربعي ({yearLabel})</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={quarterly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="quarter" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={chartFmt} width={54} />
                  <Tooltip formatter={(v) => `${chartFmt(v)} ⃁`} /><Legend wrapperStyle={{ fontSize: 12 }} />
                  {platforms.map((p, i) => <Bar key={p} dataKey={p} stackId="q" fill={COLORS[i % COLORS.length]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Platform share pie */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="mb-3 font-heading text-sm font-bold text-slate-800">مقارنة المنصات ونسبها ({yearLabel})</h3>
              {byPlatform.length === 0 ? <div className="py-16 text-center text-slate-400">لا توجد بيانات</div> : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={byPlatform} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                      label={(e) => `${e.name} ${yearTotal ? Math.round((e.value / yearTotal) * 100) : 0}%`} labelLine={false}>
                      {byPlatform.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${chartFmt(v)} ⃁`} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          </div>

          {/* Platform cards — click for a dedicated analytics page */}
          <div>
            <h3 className="mb-2 font-heading text-sm font-bold text-slate-800">المنصات — اضغط لعرض صفحة تحليل مستقلة</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {platforms.map((p, i) => {
                const yv = byPlatform.find((b) => b.name === p)?.value || 0;
                const pct = yearTotal ? Math.round((yv / yearTotal) * 100) : 0;
                return (
                  <button key={p} data-testid={`revenue-platform-card-${p}`} onClick={() => setSelectedPlatform(p)}
                    className="text-right rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-emerald-300 hover:shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="rounded-full bg-slate-100 px-1.5 text-[11px] text-slate-500">{pct}%</span>
                      <span className="flex items-center gap-1.5 font-heading font-bold text-slate-800"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{p}</span>
                    </div>
                    <div className="mt-2 text-[11px] text-slate-400">إجمالي {yearLabel}</div>
                    <div className="font-heading text-lg font-bold text-emerald-700"><Money value={yv} /></div>
                    <div className="mt-1 text-[11px] text-emerald-600">عرض التفاصيل ←</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Records table */}
          <div ref={recordsRef} className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-xs">
              <span className="font-bold text-slate-700">السجلات {month ? `— ${MONTHS_AR[Number(month) - 1]}` : ""} {year === "all" ? "" : yearLabel}</span>
              <span className="text-slate-500">{recRows.length} سجل · الإجمالي <span className="font-mono-nums font-bold text-emerald-700"><Money value={recTotal} /></span></span>
            </div>
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 text-slate-500"><tr>
                <th className="px-3 py-2.5 text-right">المنصة</th><th className="px-3 py-2.5">التاريخ</th>
                <th className="px-3 py-2.5">المبلغ</th><th className="px-3 py-2.5">الفاتورة</th>
                {canEdit && <th className="px-3 py-2.5">إجراءات</th>}
              </tr></thead>
              <tbody>
                {recRows.length === 0 ? (
                  <tr><td colSpan={5} className="py-12 text-center text-slate-400">لا توجد إيرادات مسجّلة {month ? `في ${MONTHS_AR[Number(month) - 1]}` : ""} {year === "all" ? "" : `لسنة ${year}`}</td></tr>
                ) : recRows.map((r) => (
                  <tr key={r.id} data-testid={`revenue-row-${r.id}`} className="border-t border-slate-100">
                    <td className="px-3 py-2.5 text-right"><span className="font-semibold text-slate-800">{r.platform}</span>{r.note ? <span className="text-xs text-slate-400"> · {r.note}</span> : ""}</td>
                    <td className="px-3 py-2.5 text-center font-mono-nums text-slate-600" dir="ltr">{r.revenue_date}</td>
                    <td className="px-3 py-2.5 text-center font-semibold"><Money value={r.amount} /></td>
                    <td className="px-3 py-2.5 text-center">{r.photo_id ? <a href={fileUrl(r.photo_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs"><ExternalLink className="h-3.5 w-3.5" /> {r.invoice_number || "عرض"}</a> : (r.invoice_number || <span className="text-slate-300">—</span>)}</td>
                    {canEdit && <td className="px-3 py-2.5"><div className="flex items-center justify-center gap-1.5">
                      <button data-testid={`revenue-edit-${r.id}`} onClick={() => { setEditItem(r); setEntryOpen(true); }} className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100">تعديل</button>
                      <button data-testid={`revenue-delete-${r.id}`} onClick={() => del(r.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </div></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
        </TabsContent>
      </Tabs>

      <TargetSettingsDialog open={targetOpen} onOpenChange={setTargetOpen} />
      <EntryDialog open={entryOpen} onOpenChange={setEntryOpen} platforms={platforms} initial={editItem} onSaved={load} />

      <Dialog open={platCfgOpen} onOpenChange={setPlatCfgOpen}>
        <DialogContent className="sm:max-w-sm font-body" dir={dir} data-testid="revenue-platforms-dialog">
          <DialogHeader className="text-right"><DialogTitle className="font-heading text-lg">منصات الإيراد</DialogTitle>
            <DialogDescription className="text-right">أضف منصات جديدة عند الحاجة.</DialogDescription></DialogHeader>
          <div className="flex flex-wrap gap-2">{platforms.map((p) => <span key={p} className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">{p}</span>)}</div>
          <div className="mt-3 flex gap-2">
            <Input data-testid="revenue-new-platform" value={newPlat} onChange={(e) => setNewPlat(e.target.value)} placeholder="اسم منصة جديدة" onKeyDown={(e) => e.key === "Enter" && addPlatform()} />
            <Button onClick={addPlatform} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="revenue-add-platform-btn"><Plus className="h-4 w-4" /> إضافة</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const RevenuePanel = ({ canEdit }) => {
  const [unlocked, setUnlocked] = useState(false);
  if (!unlocked) return <LockGate onUnlock={() => setUnlocked(true)} />;
  return <RevenueBody canEdit={canEdit} />;
};
